/**
 * NOTE FORM MODAL — Modale di creazione nota.
 *
 * Aperto da PersonDetail/BusinessDetail (azioni rapide "+ Nuova nota" o
 * dalla timeline "+ Nuova nota" sul tab "Nota").
 *
 * Campi (schema yaml `NoteInput`):
 *  - title (required)
 *  - description (required)
 *
 * Picker "Associa ai record" centralizzato in `useAssociatedRecordsBinder`
 * + `<AssociatedRecordsField>` (vedi components/). `linkedEntity` viene
 * passato come `extraLink` ad `applyAtSubmit`.
 */

import { useState, useRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import { htmlHasText } from '@crm/shared';
import { RichTextEditor } from '../../components/RichTextEditor';
import { useToast } from '../../components/useToast';
import { useAppDispatch } from '../../store/hooks';
import { createNote } from './noteThunks';
import { getEntityFqn } from '../../entityRegistry/entityRegistry';
import { useAssociatedRecordsBinder } from '../../components/useAssociatedRecordsBinder';
import { AssociatedRecordsField } from '../../components/AssociatedRecordsField';
import type { Note, NoteCreatePayload } from './noteTypes';

interface NoteFormModalProps {
  onClose: () => void;
  onCreated?: () => void;
  /**
   * Se presente, dopo la create dispatcha una Relationship bidirezionale
   * Note<->linkedEntity cosi' la nota risulta collegata al detail da cui
   * e' stata aperta (es. PersonDetail).
   */
  linkedEntity?: { className: string; classPK: number };
}

const FORM_ID = 'note-form';

function defaultForm(): NoteCreatePayload {
  return { title: '', description: '' };
}

export default function NoteFormModal({ onClose, onCreated, linkedEntity }: NoteFormModalProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const toast = useToast();

  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<NoteCreatePayload>(() => defaultForm());
  // Rimonta il RichTextEditor (non controllato) per svuotarlo dopo un submit "keep".
  const [editorKey, setEditorKey] = useState(0);

  const noteFqn = getEntityFqn('Note');

  const { records, setRecords, applyAtSubmit, pickerProps } = useAssociatedRecordsBinder({
    mode: 'create',
    entityFqn: noteFqn ?? undefined,
    reference: linkedEntity ?? null,
  });

  const submitIntentRef = useRef<'close' | 'keep'>('close');

  const canSubmit = form.title.trim().length > 0 && htmlHasText(form.description);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const intent = submitIntentRef.current;
    submitIntentRef.current = 'close';

    setSubmitting(true);
    try {
      // Create della Note: errore qui e' BLOCCANTE — toast + early return.
      let created: Note;
      try {
        created = (await dispatch(createNote(form)).unwrap()) as Note;
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        return;
      }
      if (!noteFqn) {
        console.warn(
          '[relationship] NoteFormModal: Note non registrato nel registry — link saltati',
          { selectedRecords: records.length },
        );
      } else {
        await applyAtSubmit?.(noteFqn, created.noteId);
      }
      toast({ severity: 'success', message: t('common.saved') });
      onCreated?.();
      if (intent === 'keep') {
        setForm(defaultForm());
        setEditorKey((k) => k + 1);
        setRecords?.([]);
      } else {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  }

  const footer = (
    <>
      <Button
        type="submit"
        form={FORM_ID}
        onClick={() => { submitIntentRef.current = 'close'; }}
        variant="contained"
        disabled={submitting || !canSubmit}
      >
        {t('common.create')}
      </Button>
      <Button type="button" onClick={onClose} variant="outlined">
        {t('common.cancel')}
      </Button>
    </>
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      <DialogTitle className="app-dialog__header">
        <span className="app-dialog__title">{t('note.createTitle')}</span>
        <IconButton
          aria-label={t('common.cancel')}
          onClick={onClose}
          size="small"
          className="app-dialog__close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
        <form id={FORM_ID} onSubmit={handleSubmit} className="note-form">
          <TextField
            label={t('note.title')}
            placeholder={t('note.titlePlaceholder')}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
            fullWidth
            className="form-field"
          />

          <div className="form-field">
            <label className="field-row__label text-xs text-muted">{t('note.description')}</label>
            <RichTextEditor
              key={editorKey}
              onChange={(html) => setForm({ ...form, description: html })}
              placeholder={t('note.descriptionPlaceholder')}
            />
          </div>

          <AssociatedRecordsField
            {...pickerProps}
            records={records}
            onChange={(val) => setRecords?.(val)}
            displayMode="click-to-edit"
          />
        </form>
      </DialogContent>
      <DialogActions className="app-dialog__footer">{footer}</DialogActions>
    </Dialog>
  );
}
